import { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { DndContext, useDraggable, useDroppable } from '@dnd-kit/core';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';

import { useTheme, Theme } from '@/services/theme';
import { breakpoints } from '@/shared/styles';
import { IconButton } from '@/shared/components/IconButton';
import { Typography } from '@/shared/components/Typography';
import { Flex } from '@/shared/components/Flex';
import { useBreakpointDown } from '@/shared/utils';
import { isNotEmpty } from '@/shared/utils/guards';
import { Tooltip } from '@/shared/components/Tooltip';

import { ReactComponent as ArrowUp } from './arrow_up.svg';
import { ReactComponent as StructuredProductLeft } from './structured_product_left.svg';
import { ReactComponent as StructuredProductRight } from './structured_product_right.svg';
import { ReactComponent as GaugeBar } from './gauge_bar.svg';
import animation from './animation.json';
import BgBlur from './bg_blur2.svg';

const TooltipRoot = styled.div`
  & .tooltip_body_hidden {
    display: none;
  }
  & .tooltip_body {
    background-color: ${({ theme }: { theme: Theme }) => theme.colors.newGray5} !important;
    box-shadow: 0 1px 2px ${({ theme }: { theme: Theme }) => theme.colors.shadow4};
    max-width: 368px;
    white-space: pre-wrap;
    overflow: hidden;
    padding: 1rem 1.6rem;
    border-radius: 12px;
  }
`;

const pulse = keyframes`
  0% {
    transform: translate(0, 0px);
  }
  25% {
    transform: translate(0, -10px);
  }
  50% {
    transform: translate(0, 0px);
  }
  80% {
    transform: translate(0, 0px);
  }
  100% { 
    transform: translate(0, 0px);
  }
`;

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  font-family: ${({ theme }: { theme: Theme }) => theme.fonts.gramatika};
  font-size: 14px;
  line-height: 1.8rem;
  font-weight: 700;
  color: #111217;
  background: #ffffff;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  cursor: ${({ disabled }: { disabled: boolean }) => (disabled ? 'auto' : 'pointer')};
  text-decoration: none;
  width: max-content;
  transition: background 300ms;
  height: 32px;

  &:hover {
    opacity: 0.9;
  }

  @media (min-width: ${breakpoints.tabletLG}px) {
    font-size: 1.3rem;
    padding: 10px 15px;
    height: 40px;
  }
`;

const EmptyButton = styled.div`
  width: 100%;
  height: 100%;
  border: 1px dashed rgba(255, 255, 255, 1);
  border-radius: 100px;
  height: 32px;
  min-width: 60px;

  @media (min-width: ${breakpoints.tabletLG}px) {
    height: 40px;
    min-width: 80px;
  }
`;

const CloseButton = styled(IconButton)`
  background: #000;
  position: absolute;
  right: 0px;
  top: 0px;
  z-index: 5px;
  padding: 6px;
  transform: translate(8px, -8px);

  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const Container = styled.section`
  padding-top: 120px;
  margin-top: 60px;

  position: relative;

  background-image: url(${BgBlur});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top right;

  h2 {
    text-align: center;
  }

  @media (min-width: ${breakpoints.tabletSM}px) {
    margin-top: 150px;
  }

  .Model__droppable_container {
    position: relative;

    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    gap: 20px;

    text-align: center;

    padding: 30px 30px;

    border-radius: 70px;
    width: 100%;
    height: 100%;

    background: rgb(236, 11, 56);
    /* background: linear-gradient(180deg, rgba(78, 80, 97, 0.6) 0%, rgba(44, 45, 56, 0.6) 100%); */
    box-shadow: 0px 0px 30.3px 0px rgba(255, 248, 248, 0.25) inset;
    backdrop-filter: blur(20px);

    overflow: hidden;

    @media (min-width: ${breakpoints.tabletLG}px) {
      padding: 20px 40px;

      border-radius: 120px;

      min-height: 220px;
      height: 220px;

      gap: 40px;
    }
  }

  .Model__content_container {
    position: relative;
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (min-width: ${breakpoints.tabletLG}px) {
      margin-top: 40px;
      flex-direction: row;
    }
  }

  .Model__content_container_left {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;

    height: 100px;

    @media (min-width: ${breakpoints.tabletLG}px) {
      height: 220px;
    }
  }

  .Model__content_container_left_content {
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    gap: 16px;

    width: 150px;
    min-width: 150px;
  }

  .Model__content_container_side_divider {
    display: none;

    @media (min-width: ${breakpoints.tabletLG}px) {
      background: linear-gradient(180deg, rgba(43, 45, 55, 0.8) 0%, rgba(43, 45, 55, 0.24) 100%);
      width: 1px;
      height: 100%;
      min-height: 100%;
      display: block;
    }
  }

  .Model__content_container_left_side {
    display: none;

    @media (min-width: ${breakpoints.tabletLG}px) {
      padding-right: 10px;
      display: block;
    }

    & > svg {
      margin-top: 10px;
      transform: scale(1.2);
    }
  }

  .Model__content_container_right {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;

    height: 100px;

    @media (min-width: ${breakpoints.tabletLG}px) {
      height: 220px;
    }
  }

  .Model__content_container_right_content {
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    gap: 16px;

    width: 150px;
    min-width: 150px;
  }

  .Model__content_container_right_side {
    display: none;

    @media (min-width: ${breakpoints.tabletLG}px) {
      padding-left: 10px;
      display: block;
    }

    & > svg {
      margin-top: 10px;
      transform: scale(1.2);
    }
  }

  .Model__droppable_attributes_container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 10px;
    flex-wrap: wrap;

    @media (min-width: ${breakpoints.tabletLG}px) {
      gap: 16px;
    }
  }
`;

const ModelAttributesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;

  height: 40px;
  min-height: 40px;

  & > .draggable_pulse {
    animation: ${({ $isPulsing }: { $isPulsing: boolean }) =>
      $isPulsing
        ? css`
            ${pulse} 1.5s ease-in-out infinite;
          `
        : 'none'};

    &:nth-child(1) {
      animation-delay: 0s;
    }
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
    &:nth-child(4) {
      animation-delay: 0.6s;
    }
    &:nth-child(5) {
      animation-delay: 0.8s;
    }
  }

  &:hover {
    & > .draggable_pulse {
      animation-play-state: paused;
    }
  }

  @media (min-width: ${breakpoints.tabletLG}px) {
    gap: 16px;
  }
`;

type Attribute = 'Staking' | 'Restaking' | 'Leverage Looping' | 'Delta Neutral' | 'Lending';

type MaybeAttribute = Attribute | null;

const attributesData: { [key in Attribute]: { apr: number; risk: number } } = {
  Staking: {
    apr: 3,
    risk: 0,
  },
  Restaking: {
    apr: 5,
    risk: 1,
  },
  'Leverage Looping': {
    apr: 15,
    risk: 2,
  },
  'Delta Neutral': {
    apr: 10,
    risk: 1,
  },
  Lending: {
    apr: 2,
    risk: 0,
  },
} as const;

const attributes = Object.keys(attributesData) as Attribute[];

const riskColors = [
  'rgba(127, 255, 178, 1)',
  'rgba(255, 236, 82, 1)',
  'rgba(255, 190, 100, 1)',
  'rgba(255, 100, 105, 1)',
];

const StructuredProductDroppable = ({
  id,
  selectedAttributes,
  setSelectedAttributes,
}: {
  id: string;
  selectedAttributes: MaybeAttribute[];
  setSelectedAttributes: React.Dispatch<React.SetStateAction<MaybeAttribute[]>>;
}) => {
  const { theme } = useTheme();
  const { isOver, setNodeRef } = useDroppable({
    id,
  });

  const unselectAttribute = (attribute: MaybeAttribute) => {
    setSelectedAttributes(prev => {
      const clone = [...prev];
      const prevIndex = clone.findIndex(attr => attr === attribute);
      if (prev.length > 3) {
        clone.splice(prevIndex, 1);
      } else {
        clone[prevIndex] = null;
      }
      return clone;
    });
  };

  return (
    <div
      ref={setNodeRef}
      className="Model__droppable_container"
      style={{
        outline: isOver ? '3px solid #7FFFB2' : '3px solid rgba(44, 45, 56, 0.6)',
      }}
    >
      <Typography fontFamily="gramatika" color="primary" variant="body1" size={2.3} weight={700}>
        Structured Product
      </Typography>

      <div className="Model__droppable_attributes_container">
        {selectedAttributes.map((attribute, idx) => (
          <div
            key={`attribute_${idx}`}
            style={{ position: 'relative' }}
            onClick={() => unselectAttribute(attribute as MaybeAttribute)}
          >
            {attribute ? (
              <>
                <StyledButton theme={theme}>{attribute}</StyledButton>
                <CloseButton circle color="#fff" icon="x-cross" size={2} />
              </>
            ) : (
              <EmptyButton />
            )}
          </div>
        ))}
      </div>

      <Typography fontFamily="gramatika" color="primary" variant="body1" size={1.5} weight={400}>
        Drag yield sources here
      </Typography>
    </div>
  );
};

const AttributeDraggable = ({
  id,
  attribute,
  disabled,
}: {
  id: string;
  attribute: string;
  disabled?: boolean;
}) => {
  const { theme } = useTheme();
  const { isDragging, attributes, listeners, setNodeRef, transform } = useDraggable({
    id,
    disabled,
  });
  const style = {
    border: 'none',
    cursor: isDragging ? 'grabbing' : 'grab',
    outline: isDragging ? '3px solid rgba(255, 255, 255, 0.3)' : 'none',
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };

  return (
    <TooltipRoot theme={theme} className="draggable_pulse">
      <Tooltip
        effect="solid"
        bodyClassName={disabled ? 'tooltip_body' : 'tooltip_body_hidden'}
        innerContent={
          disabled ? (
            <Typography fontFamily="gramatika" color="primary" variant="body1">
              Delete yield source to add a new one
            </Typography>
          ) : null
        }
        iconElement={
          <StyledButton ref={setNodeRef} theme={theme} style={style} {...listeners} {...attributes}>
            {attribute}
          </StyledButton>
        }
      />
    </TooltipRoot>
  );
};

const ModelWithContext = ({
  aprScore,
  riskScore,
  selectedAttributes,
  setSelectedAttributes,
}: {
  aprScore: number;
  riskScore: number | null;
  selectedAttributes: MaybeAttribute[];
  setSelectedAttributes: React.Dispatch<React.SetStateAction<MaybeAttribute[]>>;
}) => {
  const isTablet = useBreakpointDown(breakpoints.tabletLG);

  const draggableAttributes = attributes.filter(
    attribute => !selectedAttributes.includes(attribute),
  );

  const gaugeWidth = 88;

  const riskColor = riskScore != null ? riskColors[riskScore] : undefined;

  const riskScoreColors = [undefined, undefined, undefined, undefined].map((_, idx) =>
    riskScore != null && idx <= riskScore ? riskColor : undefined,
  );

  const isDragDisabled = selectedAttributes.filter(isNotEmpty).length >= 3;

  if (isTablet) {
    return (
      <Container>
        <h2>Craft Your Vault</h2>

        <div className="Model__content_container">
          <Flex direction="row" width="100%" align="center" justify="center" gap="30px">
            <div className="Model__content_container_left">
              <div className="Model__content_container_left_content">
                <Flex
                  direction="row"
                  height="36px"
                  align="center"
                  justify="center"
                  border="2px solid rgba(51, 53, 65, 1)"
                  padding="6px 6px"
                  borderRadius="18px"
                  position="relative"
                >
                  <Flex
                    width={`${gaugeWidth}px`}
                    height="20px"
                    background={
                      aprScore > 0
                        ? 'linear-gradient(90deg, #FFCE52 0%, #7FFFB2 100%)'
                        : 'transparent'
                    }
                    borderRadius="10px"
                  />
                  <GaugeBar
                    style={{
                      position: 'absolute',
                      transition: 'left 0.3s ease-in-out',
                      left: Math.min(gaugeWidth, 4 + aprScore * gaugeWidth),
                      top: -8,
                    }}
                  />
                </Flex>

                <Typography
                  fontFamily="gramatika"
                  color="primary"
                  variant="body1"
                  size={1.6}
                  weight={700}
                >
                  Estimated APR
                </Typography>
              </div>
            </div>

            <div className="Model__content_container_right">
              <div className="Model__content_container_right_content">
                <Flex
                  direction="row"
                  width="100%"
                  height="56px"
                  align="center"
                  gap="6px"
                  justify="center"
                >
                  {riskScoreColors.map((color, idx) => (
                    <Flex
                      key={`risk_score_${idx}`}
                      width="32px"
                      height="32px"
                      borderRadius="50%"
                      border="2px solid rgba(51, 53, 65, 1)"
                      padding="8px"
                    >
                      <Flex
                        borderRadius="50%"
                        background={color}
                        width="100%"
                        height="100%"
                        style={{ transition: 'background 0.3s ease-in-out' }}
                      />
                    </Flex>
                  ))}
                </Flex>

                <Typography
                  fontFamily="gramatika"
                  color="primary"
                  variant="body1"
                  size={1.6}
                  weight={700}
                >
                  Risk Score
                </Typography>
              </div>
            </div>
          </Flex>

          <Flex direction="column" width="100%" align="center" gap="20px">
            <StructuredProductDroppable
              id="droppable"
              selectedAttributes={selectedAttributes}
              setSelectedAttributes={setSelectedAttributes}
            />

            <Flex height="100%">
              <ArrowUp />
            </Flex>

            <ModelAttributesContainer $isPulsing={draggableAttributes.length === attributes.length}>
              {draggableAttributes.length === 0 && <EmptyButton />}
              {draggableAttributes.map(attribute => (
                <AttributeDraggable
                  key={attribute}
                  id={attribute}
                  attribute={attribute}
                  disabled={isDragDisabled}
                />
              ))}
            </ModelAttributesContainer>
          </Flex>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <h2>Craft Your Vault</h2>

      <div className="Model__content_container">
        <div className="Model__content_container_left">
          <div className="Model__content_container_left_content">
            <Flex
              direction="row"
              height="36px"
              align="center"
              justify="center"
              border="2px solid rgba(51, 53, 65, 1)"
              padding="6px 6px"
              borderRadius="18px"
              position="relative"
            >
              <Flex width={`${gaugeWidth}px`} height="20px">
                <Flex
                  borderRadius="10px"
                  transition="width 0.3s ease-in-out"
                  width={aprScore > 0 ? `100%` : '0%'}
                  height="100%"
                  background="linear-gradient(90deg, #FFCE52 0%, #7FFFB2 100%)"
                />
              </Flex>
              <GaugeBar
                style={{
                  opacity: aprScore > 0 ? 1 : 0,
                  position: 'absolute',
                  transition: 'left 0.3s ease-in-out, opacity 0.3s ease-in-out',
                  left: aprScore > 0 ? Math.min(gaugeWidth + 2, 3 + aprScore * gaugeWidth) : -10,
                  top: -8,
                }}
              />
            </Flex>

            <Typography
              fontFamily="gramatika"
              color="primary"
              variant="body1"
              size={1.6}
              weight={700}
            >
              Estimated APR
            </Typography>
          </div>
        </div>

        <Flex direction="row" width="100%">
          <div className="Model__content_container_side_divider" />
          <div className="Model__content_container_left_side">
            <StructuredProductLeft />
          </div>

          <Flex direction="column" width="100%" align="center" gap="30px">
            <StructuredProductDroppable
              id="droppable"
              selectedAttributes={selectedAttributes}
              setSelectedAttributes={setSelectedAttributes}
            />

            <Flex height="100%">
              <ArrowUp />
            </Flex>

            <ModelAttributesContainer $isPulsing={draggableAttributes.length === attributes.length}>
              {draggableAttributes.length === 0 && <EmptyButton />}
              {draggableAttributes.map(attribute => (
                <AttributeDraggable
                  key={attribute}
                  id={attribute}
                  attribute={attribute}
                  disabled={isDragDisabled}
                />
              ))}
            </ModelAttributesContainer>
          </Flex>

          <div className="Model__content_container_right_side">
            <StructuredProductRight />
          </div>
          <div className="Model__content_container_side_divider" />
        </Flex>

        <div className="Model__content_container_right">
          <div className="Model__content_container_right_content">
            <Flex direction="row" width="100%" align="center" gap="6px" justify="center">
              {riskScoreColors.map((color, idx) => (
                <Flex
                  key={`risk_score_${idx}`}
                  width="32px"
                  height="32px"
                  borderRadius="50%"
                  border="2px solid rgba(51, 53, 65, 1)"
                  padding="8px"
                >
                  <Flex
                    borderRadius="50%"
                    background={color}
                    width="100%"
                    height="100%"
                    style={{ transition: 'background 0.3s ease-in-out' }}
                  />
                </Flex>
              ))}
            </Flex>

            <Typography
              fontFamily="gramatika"
              color="primary"
              variant="body1"
              size={1.6}
              weight={700}
            >
              Risk Score
            </Typography>
          </div>
        </div>
      </div>
    </Container>
  );
};

const ModelDesktop = () => {
  const [selectedAttributes, setSelectedAttributes] = useState<MaybeAttribute[]>([
    null,
    null,
    null,
  ]);

  const nonEmptyAttributes = selectedAttributes.filter(attr => attr !== null) as Attribute[];

  // sum apr / risk for all chosen attributes
  const sumApr = Object.values(attributesData).reduce((acc, attr) => acc + attr.apr, 0);
  const sumSelectedApr = nonEmptyAttributes.reduce(
    (acc, attr) => acc + attributesData[attr as Attribute].apr,
    0,
  );

  const aprScore = sumApr === 0 ? 0 : sumSelectedApr / sumApr;

  const riskScore =
    nonEmptyAttributes.length === 0
      ? null
      : Math.max(...nonEmptyAttributes.map(attr => attributesData[attr as Attribute].risk));

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (over && over.id === 'droppable') {
      setSelectedAttributes(prev => {
        const clone = [...prev];
        const emptyIndex = clone.findIndex(attr => attr === null);
        if (emptyIndex !== -1) {
          clone[emptyIndex] = active.id as MaybeAttribute;
        } else {
          clone.push(active.id as MaybeAttribute);
        }
        return clone;
      });
    }
  };

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <ModelWithContext
        aprScore={aprScore}
        riskScore={riskScore}
        selectedAttributes={selectedAttributes}
        setSelectedAttributes={setSelectedAttributes}
      />
    </DndContext>
  );
};

const MobileLottie = styled(Lottie)`
  width: 100%;
  height: 100%;

  margin-top: 40px;

  & > svg {
    max-height: 300px;
  }

  @media (min-width: ${breakpoints.mobileLG}px) {
    & > svg {
      max-height: 400px;
    }
  }
`;

const ModelMobile = () => {
  const lottieAnimationRef = useRef<LottieRefCurrentProps | null>(null);

  useEffect(() => {
    lottieAnimationRef.current?.stop();
  }, []);

  return (
    <Container>
      <h2>Craft Your Vault</h2>

      <MobileLottie
        lottieRef={lottieAnimationRef}
        animationData={animation}
        interactivity={{
          mode: 'scroll',
          actions: [
            {
              visibility: [0, 0.5],
              type: 'stop',
              frames: [10],
            },
            {
              visibility: [0.5, 1.0],
              type: 'play',
              frames: [10, 480],
            },
          ],
        }}
      />
    </Container>
  );
};

export const Model = () => {
  const isTablet = useBreakpointDown(breakpoints.tabletLG);

  return <div>{isTablet ? <ModelMobile /> : <ModelDesktop />}</div>;
};
